<template>
  <div class="ag-grid-infinite-wrapper">
    <div class="ag-grid-infinite-table-wrapper" ref="tableWrapper">
      <ag-grid-vue
        ref="table"
        :key="`table-${_uid}`"
        class="ag-grid-infinite-table"
        :style="tableStyles"
        :gridOptions="gridOptions"
        :defaultColDef="defaultColDef"
        :columnDefs="columnDefs"
        :components="components"
        :frameworkComponents="tableFrameworkComponents"
        :rowClassRules="rowClassRulesX"
        :floatingFilter="true"
        :floatingFiltersHeight="floatingFiltersHeight"
        :localeText="localeText"

        :overlayNoRowsTemplate="overlayNoRowsTemplate"

        :pinnedBottomRowData="pinnedRowsToShow"

        :rowHeight="rowHeight"
        :headerHeight="headerHeight"

        :infiniteInitialRowCount="infiniteInitialRowCount"
        :suppressColumnVirtualisation="true"
        :suppressAnimationFrame="true"
        :cacheBlockSize="pageSize"
        :cacheOverflowSize="cacheOverflowSize"
        :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
        :rowBuffer="rowBuffer"
        :blockLoadDebounceMillis="blockLoadDebounceMillis"
        :enterMovesDownAfterEdit="enterMovesDownAfterEdit"
        :singleClickEdit="singleClickEdit"
        :stopEditingWhenGridLosesFocus="stopEditingWhenGridLosesFocus"
        :suppressCellSelection="!editable"

        rowModelType="infinite"

        @grid-ready="onGridReady"
        @modelUpdated="rowsChanged"
        :sortingOrder="['desc', 'asc', null]"
        @selectionChanged="$emit('selectionChanged', $event)"
        :rowSelection='$listeners.selectionChanged ? "single" : "none"'
      />
      <slot name="after-table" />
    </div>

    <ag-grid-vue
      v-if="isExporting"
      :columnDefs="columnDefs"
      :gridOptions="exportingTableOptions"
      :rowData="exportingRows"
      ref="exportTable"
    />

  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "ag-grid-community/src/styles/ag-grid.scss";

import InputFilter         from "./filters/InputFilter";
import InputFloatingFilter from "./floating-filters/InputFilter";

import DateFilter         from "./filters/DateFilter";
import DateFloatingFilter from "./floating-filters/DateFilter";

import SelectFilter         from "./filters/SelectFilter";
import SelectFloatingFilter from "./floating-filters/SelectFilter";
import Filter               from "@/components/AgFilters/AgFilterComponent";
import FloatingFilter       from "@/components/AgFilters/AgFloatingFilterComponent";

const LOCALE_TEXT = {
  filterOoo:          'Фильтр...',
  equals:             'Равно',
  notEqual:           'Не равно',
  lessThan:           'Меньше чем',
  greaterThan:        'Больше чем',
  lessThanOrEqual:    'Меньше или равно',
  greaterThanOrEqual: 'Больше или равно',
  inRange:            'В списке',
  contains:           'Содержит',
  notContains:        'Не содержит',
  startsWith:         'Начинается с',
  endsWith:           'Заканчивается',
  andCondition:       'И',
  orCondition:        'ИЛИ',
};

export default {
  name:       "GridInfiniteScroll",
  components: {
    AgGridVue,
  },
  props:      {
    stopEditingWhenGridLosesFocus:   {
      type:    Boolean,
      dafault: true,
    },
    floatingFiltersHeight:           {
      type:    Number,
      default: 35,
    },
    headerHeight:                    {
      type:    Number,
      default: 35,
    },
    rowHeight:                       {
      type:    Number,
      default: 35,
    },
    pageSize:                        {
      type:    Number,
      default: 20,
    },
    cacheOverflowSize:               {
      type:    Number,
      default: 1,
    },
    maxConcurrentDatasourceRequests: {
      type:    Number,
      default: 1,
    },
    rowBuffer:                       {
      type:    Number,
      default: 10,
    },
    blockLoadDebounceMillis:         {
      type:    Number,
      default: 100,
    },
    columns:                         {
      type:     Array,
      required: true,
    },
    getData:                         {
      /*
       *  Must return Promise object {
       *   total,
       *   data
       * }
       * */
      type:     Function,
      required: true,
    },
    pinnedRowsGetters:               {
      type: Object,
      default() {
        return {};
      },
    },
    getPinnedRows:                   {},
    filterable:                      {
      default: true,
      type:    Boolean,
    },
    sortable:                        {
      default: true,
      type:    Boolean,
    },
    noRowsText:                      {
      default: "Данных нет",
    },
    shownColumns:                    {
      type:    Object,
      default: () => ({}),
    },
    editable:                        {
      type:    Boolean,
      default: false,
    },
    enterMovesDownAfterEdit:         {
      type:    Boolean,
      default: false,
    },
    singleClickEdit:                 {
      type:    Boolean,
      default: false,
    },
    frameworkComponents:             {},
    showPinnedRows:                  {
      type:    Boolean,
      default: true,
    },
    suppressCellSelection:           {
      default: true,
      type:    Boolean,
    },
    selectedRow:                     {},
    rowClassRules:                   {
      default:  () => ({}),
      type:     Object,
      required: false,
    },
    tableOptions:                    {
      type:     Object,
      required: false,
    },
    defaultSorting:                  {
      type: Array,
    },
  },
  data() {
    return {
      overlayNoRowsTemplate:   null,
      firstDataLoaded:         false,
      components:              null,
      gridOptions:             null,
      defaultColDef:           null,
      infiniteInitialRowCount: null,
      gridApi:                 null,
      gridColumnApi:           null,
      tableHeight:             0,
      localeText:              LOCALE_TEXT,
      pinnedRows:              [],
      filters:                 null,
      orderBy:                 null,
      isExporting:             false,
      exportingRows:           [],
      exportingTableOptions:   {},
      isPinnedRowsShown:       true,
      isMounted:               false,
      columnDefs:              null,
    };
  },
  computed: {
    tableFrameworkComponents() {
      return Object.assign({}, this.frameworkComponents, {
        agDateFilter:           DateFilter,
        agDateFloatingFilter:   DateFloatingFilter,
        agSelectFilter:         SelectFilter,
        agSelectFloatingFilter: SelectFloatingFilter,
        agInputFilter:          InputFilter,
        agInputFloatingFilter:  InputFloatingFilter,
      });
    },
    rowClassRulesX() {
      let defaultRules = {
        'ag-table-row-pined_top':    function ( params ) {
          if (params.node.rowPinned && params.node.rowPinned.toLowerCase() === 'top') {
            return true;
          }
        },
        'ag-table-row-pined_bottom': function ( params ) {
          if (params.node.rowPinned && params.node.rowPinned.toLowerCase() === 'bottom') {
            return true;
          }
        },
        'ag-table-row-pined':        function ( params ) {
          if (params.node.rowPinned) {
            return true;
          }
        },
      };

      return Object.assign(defaultRules, this.rowClassRules);
    },
    tableStyles() {
      return {
        height: this.tableHeight,
      };
    },
    pinnedRowsToShow() {
      if (this.isPinnedRowsShown) {
        return this.pinnedRows;
      } else {
        return [];
      }
    },
  },
  methods:  {
    updateColumnsVisible() {
      if (!Object.keys(this.shownColumns).length) return false;
      for (let key in this.shownColumns) {
        this.gridOptions.columnApi.setColumnVisible(key, this.shownColumns[key]);
      }
    },
    defaultCellRenderer( params, withLoader = false, callback ) {
      if (params.value !== undefined) {
        if (callback) {
          return `<div class="ag-table-cell-value-wrapper"><div class="ag-table-cell-value">${callback(params)}</div></div>`;
        } else if (params.value !== null) {
          return `<div class="ag-table-cell-value-wrapper"><div class="ag-table-cell-value">${params.value}</div></div>`;
        }
      } else if (params.column.colDef.field === 'account_number' || params.column.colDef.field === 'id' || withLoader) {
        return '<div class="ag-table-cell-value-wrapper"><div class="ag-table-cell-value">Загрузка...</div></div>';
      } else {
        return "";
      }
    },
    defaultPinnedRowCellRenderer( params ) {
      let field = params.colDef.field;
      if (typeof this.pinnedRowsGetters[field] === 'function') {
        if (params.value === undefined) {
          return '<div class="ag-table-cell-value-wrapper"><div class="ag-table-cell-value">Загрузка...</div></div>';
        } else {
          return params.colDef.cellRenderer ? params.colDef.cellRenderer(params) : params.value;
        }
      } else if (this.pinnedRowsGetters.hasOwnProperty(field)) {
        return `<div class="ag-table-cell-value-wrapper"><div class="ag-table-cell-value">${this.pinnedRowsGetters[field]}</div></div>`;
      }
    },
    defaultValueRenderer( params, callback ) {
      let field = params.colDef.field;
      let isPinned = !!params.node.rowPinned;

      if (isPinned) {
        if (typeof this.pinnedRowsGetters[field] === 'function') {
          if (params.data === undefined || params.data[field] === undefined) {
            return undefined;
          } else {
            return this.pinnedRowsGetters[field](params.data);
          }
        } else if (this.pinnedRowsGetters.hasOwnProperty(field)) {
          return this.pinnedRowsGetters[field];
        }
      } else {
        if (params.data === undefined) {
          return undefined;
        } else {
          return callback(params);
        }
      }
    },
    mapColumns( columns ) {
      let columnDefs = columns.map(column => {
        column.suppressSizeToFit = column.suppressSizeToFit !== false;
        if (column.colId === undefined) {
          column.colId = column.field;
        }

        if ((!column.cellRendererFramework && column.cellRenderer === undefined) || typeof column.cellRenderer === "function") {
          let columnCellRenderer = column.cellRenderer;
          column.cellRenderer = ( params ) => {
            return this.defaultCellRenderer(params, column.withLoading, columnCellRenderer);
          };
        }

        delete column.withLoading;

        if (column.pinnedRowCellRenderer === undefined) {
          column.pinnedRowCellRenderer = this.defaultPinnedRowCellRenderer;
        }

        if (typeof column.valueGetter === "function") {
          let columnValueGetter = column.valueGetter;
          column.valueGetter = ( params ) => {
            return this.defaultValueRenderer(params, columnValueGetter);
          };
        }
        //SET FILTERS
        if ((this.filterable && column.filter === undefined) || column.filter !== undefined && column.filter !== false) {
          let filterName = typeof column.filter === 'string' ? column.filter.trim().toLowerCase() : 'text';
          let filterComponent = null;
          let filterComponentParams = {
            localeText:             this.localeText,
            suppressAndOrCondition: true,
            applyButton:            true,
          };
          let floatingFilterComponent = null;
          let floatingFilterComponentParams = {};
          switch (filterName) {
            case 'date':
            case 'datefilter':
            case 'agdatefilter':
              filterComponent = 'agDateFilter';
              floatingFilterComponent = 'agDateFloatingFilter';
              floatingFilterComponentParams.suppressFilterButton = true;
              filterComponentParams.filterOptions = ['equals'];
              filterComponentParams.defaultType = 'equals';
              break;
            case 'select':
            case 'selectfilter':
            case 'agselectfilter':
              filterComponent = 'agSelectFilter';
              floatingFilterComponent = 'agSelectFloatingFilter';
              floatingFilterComponentParams.suppressFilterButton = true;
              filterComponentParams.filterOptions = ['equals'];
              filterComponentParams.defaultType = 'equals';
              break;
            case 'number':
            case 'numberfilter':
            case 'agnumberfilter':
              filterComponent = 'agInputFilter';
              floatingFilterComponent = 'agInputFloatingFilter';
              filterComponentParams.filterOptions = ['equals', 'notEqual', 'lessThan', 'lessThanOrEqual', 'greaterThan', 'greaterThanOrEqual'];
              filterComponentParams.inputType = 'double';
              filterComponentParams.defaultType = 'equals';
              break;
            case 'text':
            case 'textFilter':
            default:
              filterComponent = 'agInputFilter';
              floatingFilterComponent = 'agInputFloatingFilter';
              filterComponentParams.filterOptions = ['equals', 'notEqual', 'contains', 'notContains', 'startsWith', 'endsWith'];
              filterComponentParams.inputType = 'text';
              filterComponentParams.defaultType = 'contains';
              break;
          }
          column.filter = filterComponent;
          column.filterParams = Object.assign({}, column.filterParams, filterComponentParams);
          column.floatingFilterComponent = floatingFilterComponent;
          column.floatingFilterComponentParams = Object.assign({}, column.floatingFilterComponentParams, floatingFilterComponentParams);
        }
        return column;
      });

      return columnDefs;
    },
    onGridReady( params ) {
      params.api.setDatasource({
        rowCount: null,
        getRows:  async ( params ) => {
          let page = params.endRow / this.pageSize;

          let orderBy = [];
          let filters = [];
          if (params.sortModel.length) {
            let sortModel = params.sortModel[0];
            orderBy = [sortModel.colId, sortModel.sort.charAt(0).toUpperCase() + sortModel.sort.slice(1)];
          }
          // consoleg('filterModel', params.filterModel);
          if (params.filterModel) {
            for (let filterIndex in params.filterModel) {
              if (params.filterModel.hasOwnProperty(filterIndex)) {
                let filter = params.filterModel[filterIndex];
                if (filter.filter !== null) {
                  filters.push([filterIndex, filter.type, filter.filter]);
                }
              }
            }
          }

          let query = Object.assign({}, this.$route.query);
          let needChangeRoute = false;
          let filterUpdates = false;

          if ((filters.length && this.$route.query.filters !== JSON.stringify(filters))) {
            query.filters = JSON.stringify(filters);
            needChangeRoute = true;
            filterUpdates = true;
          } else if (!filters.length && this.$route.query.filters) {
            delete query.filters;
            needChangeRoute = true;
            filterUpdates = true;
          }
          if ((orderBy.length && JSON.stringify(this.$route.query.orderBy) !== JSON.stringify(orderBy))) {
            query.orderBy = orderBy;
            needChangeRoute = true;
          } else if (!orderBy.length && this.$route.query.orderBy) {
            delete query.orderBy;
            needChangeRoute = true;
          }
          if (needChangeRoute) {
            // console.log(query);
            this.$router.replace({ query: query });
          }
          if (!this.firstDataLoaded || filterUpdates) {
            this.updatePinnedRows(JSON.stringify(filters), orderBy);
          }
          this.gridApi.hideOverlay();
          this.filters = JSON.stringify(filters);
          this.orderBy = orderBy;
          this.getData(page, JSON.stringify(filters), orderBy).then(( { data, total } ) => {
            this.gridApi.setInfiniteRowCount(total, true);
            if (total === 0) {
              this.gridApi.showNoRowsOverlay();
            }
            params.successCallback(data, total);
          });

          if (!this.firstDataLoaded) {
            this.firstDataLoaded = true;
          }
        },
      });
    },
    resetData() {
      this.gridApi.hideOverlay();
      this.firstDataLoaded = false;
      this.gridApi.purgeInfiniteCache();
      this.gridApi.deselectAll();
      if (this.gridApi.getDisplayedRowCount()) this.gridApi.ensureIndexVisible(0);
    },
    sizeToFit() {
      this.gridApi.sizeColumnsToFit();
    },
    rowsChanged() {
      function debounce( func, wait, immediate ) {
        var timeout;
        return function () {
          var context = this, args = arguments;
          var later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
          };
          var callNow = immediate && !timeout;
          clearTimeout(timeout);
          timeout = setTimeout(later, wait);
          if (callNow) func.apply(context, args);
        };
      };


      debounce(this.autoSizeAll(), 1000);
    },
    autoSizeAll( skipHeader = false ) {
      let allColumnIds = [];
      this.gridColumnApi.getAllColumns().forEach(function ( column ) {
        if (column.colDef.suppressSizeToFit !== true) {
          allColumnIds.push(column.colId);
        }
      });
      this.gridColumnApi.autoSizeColumns(allColumnIds, skipHeader);
    },
    setSort() {
      if (this.defaultSorting) {
        const [colId, sort] = this.defaultSorting;
        return this.gridApi.setSortModel([{
          colId,
          sort: sort.toLowerCase(),
        }]);
      }
      if (this.$route.query.orderBy) {
        try {
          let sortArray = this.$route.query.orderBy;

          let sort = [
            {
              colId: sortArray[0],
              sort:  sortArray[1].toLowerCase(),
            },
          ];
          this.gridApi.setSortModel(sort);
        } catch (e) {
          console.error('ERROR', e);
        }
      }
    },
    setFilters() {
      if (this.$route.query.filters) {
        let filters = [];

        try {
          filters = JSON.parse(this.$route.query.filters);
          let applyFilters = {};
          filters.forEach(filter => {
            let value = filter[2];
            let filterType = filter[1];
            applyFilters[filter[0]] = {
              type:   filterType,
              filter: value,
            };
          });
          // console.log({ applyFilters });
          this.gridApi.setFilterModel(applyFilters);
          this.gridApi.onFilterChanged();
        } catch (e) {
          console.error('ERROR', e);
        }
      }
    },
    initPinnedRows() {
      if (Object.keys(this.pinnedRowsGetters).length) {
        let pinnedRow = {};

        for (let key in this.pinnedRowsGetters) {
          if (this.pinnedRowsGetters.hasOwnProperty(key)) {
            if (typeof this.pinnedRowsGetters[key] !== 'function') {
              pinnedRow[key] = this.pinnedRowsGetters[key];
            } else {
              pinnedRow[key] = undefined;
            }
          }
        }

        this.pinnedRows = [pinnedRow];
      } else {
        this.pinnedRows = [];
      }
    },
    updatePinnedRows( filter, orderBy ) {
      if (typeof this.getPinnedRows === 'function') {
        this.initPinnedRows();
        this.getPinnedRows(filter, orderBy).then(( data ) => {
          this.pinnedRows = [...data];
        });
      }
    },
    exportRows( fileName = 'Экспорт' ) {
      this.isExporting = true;
      return this.getData(undefined, this.filters, this.orderBy).then(( { response } ) => {
        this.exportingRows = response.data || response;
        this.$nextTick(() => {
          const columnKeys = this.$refs.table.columnDefs.map(c => c.colId).filter(key => key !== 'actions');
          this.exportingTableOptions.api.exportDataAsCsv({
            fileName:            fileName,
            columnKeys,
            columnSeparator:     ';',
            processCellCallback: ( params ) => {
              let value = params.value;
              if (params.column.colDef && params.column.colDef.valueGetter) {
                let cellParams = {
                  data:      params.node.data, // the data you provided for this row
                  node:      params.node, // the row node for this row
                  colDef:    params.column.colDef, // the column def for this column
                  column:    params.column, // the column for this column
                  api:       params.api, // the grid API
                  columnApi: params.gridApi, // the grid Column API
                  context:   params.context,  // the context,
                  forExport: true,
                };
                value = params.column.colDef.valueGetter(cellParams, true);
              }

              return value ? value.toLocaleString() : value;
            },
          });
        });
      });
    },
    refreshCells() {
      this.gridApi.refreshCells();
    },
    refreshRows() {
      this.gridApi.redrawRows();
    },
    hidePinnedRows() {
      this.isPinnedRowsShown = false;
    },
    openPinnedRows() {
      this.isPinnedRowsShown = true;
    },
  },
  beforeMount() {
    this.overlayNoRowsTemplate = `<span style="font-weight: 500; font-size: 1.2rem;">${this.noRowsText}</span>`;
    this.initPinnedRows();
    this.components = {};
    this.gridOptions = {};
    this.infiniteInitialRowCount = 4;
    this.defaultColDef = {
      filter:    this.filterable,
      sortable:  this.sortable,
      editable:  this.editable,
      resizable: true,
      flex:      1,
      // minWidth: 50
    };
    this.columnDefs = this.mapColumns(this.columns);
    this.firstDataLoaded = false;
    this.isPinnedRowsShown = this.showPinnedRows;
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
    this.tableHeight = this.$refs.tableWrapper.clientHeight;
    this.updateColumnsVisible();
    this.isMounted = true;
    this.setFilters();
    this.setSort();
    if (this.tableOptions) Object.assign(this.tableOptions, this.gridOptions);
  },
  beforeDestroy() {
  },
  watch: {
    '$route.query'( value, prevValue ) {
      // console.log(value);
    },
    shownColumns: {
      handler() {
        this.updateColumnsVisible();
        this.$emit('updateVisibleColumns', this.shownColumns);
      },
      deep: true,
    },
    columns() {
      this.gridApi.setColumnDefs([]);
      this.$nextTick(() => {
        this.gridApi.setColumnDefs(this.mapColumns(this.columns));
        this.$nextTick(() => {
          this.rowsChanged();
          this.setFilters();
          this.setSort();
          this.updateColumnsVisible();
        });
      });
    },
    showPinnedRows() {
      this.isPinnedRowsShown = this.showPinnedRows;
    },
  },
};
</script>

<style lang="scss">
.ag-grid-infinite-wrapper {
  flex: 1;
  display: flex;
  flex-flow: column nowrap;
}

.ag-grid-infinite-table-wrapper {
  flex: 1;
  display: flex;
  flex-flow: column nowrap;

  .ag-row {
    border-bottom: 1px solid rgba(0, 0, 0, .1);
  }

  .ag-cell {
    user-select: text;
  }
}

.ag-grid-infinite-table {
  flex: 1;
  display: flex;
  flex-direction: column;

  .ag-cell-focus {
    border: 1px solid rgba(var(--app-primary), 1);

    &.ag-cell-inline-editing {
      box-shadow: 0 3px 1px -2px rgba(0, 0, 0, .2), 0 2px 2px 0 rgba(0, 0, 0, .14), 0 1px 5px 0 rgba(0, 0, 0, .12);
      background-color: #fff;
      padding: 0;
    }
  }

  .ag-row-odd {
    background: #f5f8fa;
  }

  .ag-row-danger {
    background: rgba(255, 0, 0, .07) !important;
  }

  .ag-header-cell-label {
    justify-content: center;
  }

  .ag-header-icon {
    margin-left: 5px;
  }

  .ag-icon {
    width: 16px;
    height: 16px;
    background-position: center;
    background-size: contain;
  }

  .ag-floating-filter-button-button {
    appearance: none;
    background: transparent;
    border: none;
    box-shadow: none;
    cursor: pointer;
    margin-left: 5px;
  }

  .ag-menu {
    box-shadow: 5px 5px 10px rgba(0, 0, 0, .3);
    background: #fff;
    border-radius: 2px;
  }

  .ag-header-cell-menu-button {
    display: none;
  }

  .ag-pinned-left-cols-container, .ag-pinned-left-header {
    border-right: 1px solid rgba(0, 0, 0, .1);
    min-height: 100%;
  }

  .ag-pinned-left-floating-bottom {
    border-right: 1px solid rgba(0, 0, 0, .1);
  }

  .ag-floating-bottom {
    border-top: 1px solid rgba(0, 0, 0, .1);
  }

  .ag-icon-filter {
    background-image: url('~@/assets/images/ag-grid/dark-filter.svg');
  }

  .ag-icon-asc {
    background-image: url('~@/assets/images/ag-grid/dark-asc.svg');
  }

  .ag-icon-desc {
    background-image: url('~@/assets/images/ag-grid/dark-desc.svg');
  }

  .ag-table-row-pined {
    font-weight: 600;
  }

  .ag-table-cell-tag {
    padding: 5px;
    font-size: 10px;
    line-height: 1.1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .ag-cell-value-link {
    color: rgba(var(--app-content-color), 1);
    cursor: pointer;

    &:hover {
      color: rgba(var(--app-primary), 1);
      text-decoration: underline;
    }
  }
}

.ag-header-cell {
  font-weight: bold;
  text-align: left;
  vertical-align: bottom;
  padding: 0 10px;
  background: transparent;
  font-size: 11px;
  border-bottom: 1px solid rgba(0, 0, 0, .125);

  &-text {
    white-space: normal;
    text-align: center;
    line-height: 1.2;
    font-size: 11px;
  }
}

.ag-cell-value {
  padding: .5rem 10px;
  font-size: 12px;

  & > span {
    display: block;
    height: 100%;
  }
}

.ag-table-cell-value-wrapper {
  display: block;
  height: 100%;
}

.ag-table-cell-value {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1.1;
}

.ag-cell-auto-height {
  white-space: normal;
}

.ag-table-row-pined_bottom {
  .ag-cell-value > span {
    display: flex;
    text-align: center;
    align-items: center;
    justify-content: center;
  }
}

.ag-root-wrapper.ag-layout-normal {
  flex: 1;
}

.ag-grid-infinite-table .ag-row-selected {
  background: rgba(139, 212, 238, .2);
}


</style>
